/**
 * Authentication models.
 *
 * Merchant login is a **two-phase** protocol:
 *
 *  1. `POST /auth/verify-merchant-login` resolves the username to the user's *primary*
 *     account, which is what the password is first checked against.
 *  2. `POST /auth/login` then either completes the session (one merchant) or refuses to
 *     issue a token and returns the list of merchants to choose from (several). In the
 *     second case the client calls `POST /account/session` with the same credential and
 *     the chosen account to obtain the real session.
 *
 * See `multi-merchant-login-architecture.md` for the full decision tree.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Credentials & verification
// ─────────────────────────────────────────────────────────────────────────────

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface VerifyMerchantLoginRequest {
  username: string;
  type: string;
  device_type: string;
}

export interface VerifyMerchantLoginResponse {
  success: boolean;
  /** The backend sends this as a JSON number (`3`), not a string. */
  message?: string | number;
  /**
   * Always the account row of the user's **primary** merchant — never the one they will
   * end up choosing. It exists so the password screen has something to post against.
   */
  account_id: string;
  /**
   * Country-configured login methods, e.g. `['password']`. Web only supports `password`;
   * the field is typed correctly so the OTP work does not start from a lie.
   */
  merchant_login_method: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Country
// ─────────────────────────────────────────────────────────────────────────────

export interface CountryDetail {
  is_referral: boolean;
  display_currency: number;
  display_decimal: number;
  display_time: number;
  countryname: string;
  countryphonecode: string;
  phone_number_length: number;
  flag_url: string;
  currencycode: string;
  providerreferral: boolean;
  bonus_to_providerreferral: number;
  referral_bonus_to_provider: number;
  /** Singular string here, unlike the array on `VerifyMerchantLoginResponse`. */
  merchant_login_method: string;
  merchant_gozem_money_enabled: boolean;
  gozem_money_enabled: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Accounts
// ─────────────────────────────────────────────────────────────────────────────

/**
 * An account row exactly as the backend sends it — **two different shapes for the same
 * concept**, depending on which login branch produced it:
 *
 * | Meaning                     | success branch | pending branch     |
 * |-----------------------------|----------------|--------------------|
 * | the business (`Merchant`)   | `_id`          | `merchant_id`      |
 * | the membership row          | `account_id`   | `account_id`       |
 * | the person (`MerchantUser`) | *(top level)*  | `merchant_user_id` |
 *
 * Verified against real payloads: in the success branch `accounts[0]._id` is byte-for-byte
 * the top-level `merchant_id`. So `_id` is **not** an account id — reading it as one is the
 * bug that made the app post a merchant id into every `account_id` field.
 *
 * Every field is optional because the pending branch omits most of them. Never consume this
 * type directly — run it through `normalizeMerchantAccounts` and use `MerchantAccount`.
 */
export interface RawMerchantAccount {
  /** Success branch only. This is the **merchant** id, despite the name. */
  _id?: string;
  /** Pending branch only. Same value as `_id` above, honestly named. */
  merchant_id?: string;
  /** The `MerchantAccountUser` row id. Present in both branches. The real account id. */
  account_id?: string;
  merchant_user_id?: string;
  name?: string;
  image?: string;
  logo?: string;
  address?: string;
  /** The **business** email, not the login identity. Differs per account for one user. */
  email?: string;
  country_id?: string;
  city_id?: string;
  role?: string;
  is_default_merchant?: boolean;
  migrate_to_mobile_money?: boolean;
}

/**
 * The canonical in-app account shape. Produced only by `normalizeMerchantAccounts`, which
 * is the single boundary where the two raw shapes above are reconciled.
 *
 * Every field is required and guaranteed non-null, so consumers can call `.toLowerCase()`
 * and friends without guards.
 */
export interface MerchantAccount {
  /** `MerchantAccountUser._id`. The value that belongs in an `account_id` request field. */
  accountId: string;
  /** `Merchant._id`. The business this account grants access to. */
  merchantId: string;
  merchantUserId: string;
  name: string;
  image: string;
  logo: string;
  address: string;
  email: string;
  countryId: string;
  cityId: string;
  role: string;
  isDefaultMerchant: boolean;
  migrateToMobileMoney: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tokens
// ─────────────────────────────────────────────────────────────────────────────

export interface TokenData {
  access_token: string;
  refresh_token: string;
  token_type: string;
  /** Seconds. Observed: 3600 (1 h). */
  expires_in: number;
  /** Seconds. Observed: 604800 (7 days) — not the 2 h the mobile write-up states. */
  refresh_expires_in: number;
  client_id: string;
  user_type: number;
}

export interface RefreshTokenResponse {
  data: {
    access_token: string;
    token_type: string;
    expires_in: number;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Login responses — discriminated on `login_successful`
// ─────────────────────────────────────────────────────────────────────────────

interface LoginResponseBase {
  success: boolean;
  /** A JSON number in practice (`3`), despite reading like a message string. */
  message: string | number | null;
}

/**
 * The session payload. Returned by `/auth/login` when the user has exactly one merchant,
 * and by `/account/session` once a merchant has been chosen — the two are the same shape,
 * which is why `establishSession` can consume both.
 */
export interface LoginSuccessResponse extends LoginResponseBase {
  login_successful: true;
  token: string;
  token_data: TokenData;
  country_detail: CountryDetail;
  /** ~70 permission strings. Not consumed on web (decision D5). */
  access_rules: string[];
  onboarding_status: string;
  merchant_id: string;
  merchant_user_id: string;
  merchant_name: string;
  merchant_user_name: string;
  address: string;
  app_version: string;
  created_at: string;
  email: string;
  phone: string;
  picture: string;
  is_approved: boolean;
  first_name: string;
  accounts: RawMerchantAccount[];
}

/**
 * Credentials were accepted but the session cannot be completed until the user picks a
 * merchant. **No token of any kind is present** — that is the whole point of the branch,
 * and why `token_data` is absent from this type rather than optional on a shared one.
 */
export interface LoginAccountSelectionResponse extends LoginResponseBase {
  login_successful: false;
  accounts: RawMerchantAccount[];
}

export type LoginResponse = LoginSuccessResponse | LoginAccountSelectionResponse;

/**
 * Narrow a login response to the branch that carries a session.
 *
 * Deliberately tests `=== true` rather than truthiness: an absent or malformed
 * `login_successful` then falls to the *pending* branch, where the worst outcome is an
 * unnecessary account picker. Narrowing the other way would read `token_data` off
 * `undefined` — the exact crash this union exists to prevent.
 */
export function isLoginSuccess(response: LoginResponse): response is LoginSuccessResponse {
  return response?.login_successful === true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Account session (phase 2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * How the credential in `pass` was obtained. The endpoint is shared with the mobile OTP
 * flow; web always sends `'password'`.
 */
export type AccountSessionGateway = 'sms' | 'email' | 'password' | 'whatsapp';

/**
 * Body of `POST /api_merchant/v1/account/session`.
 *
 * A parameter object rather than positional arguments: `credential` and `gateway` are both
 * strings, adjacent, and silently transposable at a call site (SonarQube S107).
 *
 * Note `app_version` is **not** part of this contract even though `/auth/login` accepts it.
 */
export interface EnterAccountSessionParams {
  /** The chosen `MerchantAccount.accountId` — never a merchant id. */
  accountId: string;
  /** The same password the user typed on the login screen. */
  credential: string;
  gateway: AccountSessionGateway;
}

// ─────────────────────────────────────────────────────────────────────────────
// Misc
// ─────────────────────────────────────────────────────────────────────────────

export interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
}

export interface MerchantDetailsRequest {
  account_id: string;
}
